import React from 'react';
import { EmailData } from '../../../types/results.types';

export interface DecisionMakerPreviewPerson {
  id: number;
  first_name: string;
  last_name?: string | null;
  role: string;
  corrected_first_name?: string | null;
  corrected_last_name?: string | null;
  corrected_role?: string | null;
  emails?: EmailData[];
  is_unlocked?: boolean;
}

interface DecisionMakerPreviewStackProps {
  people: DecisionMakerPreviewPerson[];
  forceGray?: boolean;
  roleMaxWidthClassName?: string;
}

const SUCCESS = '#059669';
const DARK = '#1C1B22';
const MID = '#6B7280';

function getInitials(person: DecisionMakerPreviewPerson): string {
  const first = person.corrected_first_name || person.first_name || '';
  const last = person.corrected_last_name || person.last_name || '';
  return ((first[0] || '') + (last[0] || '')).toUpperCase() || '?';
}

function getRole(person: DecisionMakerPreviewPerson): string {
  return person.corrected_role || person.role || '';
}

function isVerified(person: DecisionMakerPreviewPerson): boolean {
  return Boolean(person.emails?.some((email) => email.verification_status === 'GUARANTEED'));
}

function avatarBg(person: DecisionMakerPreviewPerson, forceGray: boolean): string {
  if (forceGray) return '#D1D5DB';
  if (!person.is_unlocked) return '#D1D5DB';
  if (isVerified(person)) return SUCCESS;
  if ((person.emails?.length ?? 0) > 0) return DARK;
  return '#9CA3AF';
}

const Avatar: React.FC<{ initials: string; size?: number; bg?: string }> = ({
  initials,
  size = 26,
  bg = '#D1D5DB',
}) => (
  <div
    className="flex-shrink-0 flex items-center justify-center rounded-full font-bold select-none text-white"
    style={{ width: size, height: size, background: bg, fontSize: size * 0.34 }}
  >
    {initials}
  </div>
);

const DecisionMakerPreviewStack: React.FC<DecisionMakerPreviewStackProps> = ({
  people,
  forceGray = false,
  roleMaxWidthClassName = 'max-w-[140px]',
}) => {
  const visible = people.slice(0, 3);
  const extra = people.length - 3;
  const uniqueRoles = [...new Set(people.map((person) => getRole(person)).filter(Boolean))].slice(0, 2);
  const rolesLabel = uniqueRoles.join(', ') + (people.length > uniqueRoles.length ? ' + more' : '');

  if (people.length === 0) return null;

  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center flex-shrink-0">
        {visible.map((person, index) => (
          <div
            key={person.id}
            className="rounded-full border-2 border-white"
            style={{ marginLeft: index === 0 ? 0 : -9, zIndex: 3 - index, position: 'relative' }}
          >
            <Avatar initials={getInitials(person)} bg={avatarBg(person, forceGray)} />
          </div>
        ))}
        {extra > 0 && (
          <div
            className="rounded-full border-2 border-white flex items-center justify-center"
            style={{
              marginLeft: -9,
              width: 26,
              height: 26,
              background: '#F0EDE8',
              fontSize: 9,
              fontWeight: 700,
              color: MID,
              position: 'relative',
              zIndex: 0,
            }}
          >
            +{extra}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-semibold text-[#1C1B22]">
          {people.length} {people.length === 1 ? 'person' : 'people'}
        </div>
        {rolesLabel && (
          <div className={`mt-0.5 truncate text-[10.5px] text-[#6B7280] ${roleMaxWidthClassName}`}>
            {rolesLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default DecisionMakerPreviewStack;
